import { tablesDB, DATABASE_ID, Query, ID } from '../lib/appwrite';

const VEHICLE_TYPES_TABLE_ID = 'vehicleTypes';
const DELIVERY_SETTINGS_TABLE_ID = 'deliverySettings';
const GLOBAL_SETTINGS_ID = 'settings_global';

// ─── Vehicle Types ───────────────────────────────────────────────────────────

export async function listVehicleTypes(activeOnly = false) {
  const queries = [Query.limit(100)];
  if (activeOnly) queries.push(Query.equal('isActive', true));
  const res = await tablesDB.listRows(DATABASE_ID, VEHICLE_TYPES_TABLE_ID, queries);
  return res.rows || [];
}

export async function getVehicleType(id) {
  return tablesDB.getRow(DATABASE_ID, VEHICLE_TYPES_TABLE_ID, id);
}

export async function createVehicleType(data) {
  const rowId = `vt_${ID.unique()}`;
  return tablesDB.createRow(DATABASE_ID, VEHICLE_TYPES_TABLE_ID, rowId, {
    name: data.name,
    code: data.code || data.name.toLowerCase().replace(/\s+/g, '_'),
    description: data.description || '',
    baseFee: parseFloat(data.baseFee) || 0,
    pricePerKm: parseFloat(data.pricePerKm) || 0,
    maxWeightKg: parseFloat(data.maxWeightKg) || 0,
    isActive: data.isActive !== false,
  });
}

export async function updateVehicleType(id, data) {
  const update = {};
  if (data.name !== undefined) update.name = data.name;
  if (data.code !== undefined) update.code = data.code;
  if (data.description !== undefined) update.description = data.description;
  if (data.baseFee !== undefined) update.baseFee = parseFloat(data.baseFee) || 0;
  if (data.pricePerKm !== undefined) update.pricePerKm = parseFloat(data.pricePerKm) || 0;
  if (data.maxWeightKg !== undefined) update.maxWeightKg = parseFloat(data.maxWeightKg) || 0;
  if (data.isActive !== undefined) update.isActive = data.isActive;
  return tablesDB.updateRow(DATABASE_ID, VEHICLE_TYPES_TABLE_ID, id, update);
}

export async function deleteVehicleType(id) {
  return tablesDB.deleteRow(DATABASE_ID, VEHICLE_TYPES_TABLE_ID, id);
}

// ─── Delivery Settings ────────────────────────────────────────────────────────

export async function getDeliverySettings() {
  try {
    return await tablesDB.getRow(DATABASE_ID, DELIVERY_SETTINGS_TABLE_ID, GLOBAL_SETTINGS_ID);
  } catch {
    return null;
  }
}

export async function updateDeliverySettings(data) {
  const update = {};
  if (data.defaultBaseFee !== undefined) update.defaultBaseFee = parseFloat(data.defaultBaseFee) || 0;
  if (data.defaultPricePerKm !== undefined) update.defaultPricePerKm = parseFloat(data.defaultPricePerKm) || 0;
  if (data.minFee !== undefined) update.minFee = parseFloat(data.minFee) || 0;
  if (data.maxDistanceKm !== undefined) update.maxDistanceKm = parseFloat(data.maxDistanceKm) || 0;
  // What a buyer is charged at checkout. These used to be written into the
  // buyer app, so changing one meant a release and a wait for every phone;
  // the app reads them from here, through the backend, now.
  if (data.buyerBaseFeePerStore !== undefined) update.buyerBaseFeePerStore = parseFloat(data.buyerBaseFeePerStore) || 0;
  if (data.buyerPricePerKm !== undefined) update.buyerPricePerKm = parseFloat(data.buyerPricePerKm) || 0;
  if (data.buyerPricePerKg !== undefined) update.buyerPricePerKg = parseFloat(data.buyerPricePerKg) || 0;
  if (data.buyerMissingLocationFee !== undefined) update.buyerMissingLocationFee = parseFloat(data.buyerMissingLocationFee) || 0;
  if (data.currency !== undefined) update.currency = data.currency;
  if (data.isActive !== undefined) update.isActive = data.isActive;
  if (data.notes !== undefined) update.notes = data.notes;
  return tablesDB.updateRow(DATABASE_ID, DELIVERY_SETTINGS_TABLE_ID, GLOBAL_SETTINGS_ID, update);
}

// ─── Price Calculator ─────────────────────────────────────────────────────────

export function calculateDeliveryPrice(distanceKm, vehicleType, settings) {
  const baseFee = parseFloat(vehicleType?.baseFee ?? settings?.defaultBaseFee ?? 2000);
  const perKm = parseFloat(vehicleType?.pricePerKm ?? settings?.defaultPricePerKm ?? 500);
  const minFee = parseFloat(settings?.minFee ?? 1500);
  const calculated = baseFee + distanceKm * perKm;
  return Math.max(calculated, minFee);
}

// Haversine distance between two lat/lng points (returns km)
export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
